import { PlayArrow } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Paper, Tab, Tabs, Typography } from '@mui/material'
import { useAudioEngine, type MidiNoteEvent } from '@tunasong/audio-ui'
import { logger } from '@tunasong/models'
import type { Chord, ChordVariant, NoteEvent, NoteOctave } from '@tunasong/music-lib'
import { ChordLib, getNoteAndOctave, GuitarLib, NoteLib, ScaleLib, symbols } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import { Divider, useDimensions, VBox } from '@tunasong/ui-lib'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Fretboard } from '../guitar/visualization/fretboard'
import { GuitarChord } from '../guitar/visualization/guitar-chord'
import { Keyboard } from '../keyboard/keyboard'
import { Note } from '../note/note'
import { usePlayNotes } from '../player/piano-player.hook.js'
import CircleOfFifths from '../scale/circle-of-fifths.js'

interface MusicTheoryProps {
  scale: Scale
}

export const MusicTheory = ({ scale }: MusicTheoryProps) => {
  const [selectedTab, setSelectedTab] = useState(0)
  const [selectedChord, setSelectedChord] = useState<ChordVariant | null>(null)

  const { playNotes, playNoteSequence } = usePlayNotes()

  // Get the audio mixer for playing sounds
  const { isSmallOrSmaller } = useDimensions()

  // Generate fretboard fingerings based on scale
  const fingeringsFromScale = scale ? GuitarLib.getFingeringsForScale({ scale }) : []

  // Get scale notes
  const scaleNotes = scale ? ScaleLib.getNotesWithOctave(scale, 4) : []

  // Get scale chords
  const scaleChords = scale ? ScaleLib.getChords(scale) : []

  const [activeNotes, setActiveNotes] = useState<NoteOctave[]>([])

  const engine = useAudioEngine()
  useEffect(() => {
    if (!engine) {
      return
    }
    const handlePlay = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return
      }
      const noteEvent = event.detail as MidiNoteEvent
      const { note: noteSpec, type } = noteEvent

      const note = `${noteSpec.name}${noteSpec.accidental ? noteSpec.accidental : ''}${noteSpec.octave}` as NoteOctave

      logger.debug('Received note event:', noteEvent, note)

      if (type === 'noteon') {
        setActiveNotes(prev => [...new Set([...prev, note])])
      } else if (type === 'noteoff') {
        setActiveNotes(prev => prev.filter(n => n !== note))
      }
    }
    engine.addEventListener('midievent', handlePlay)
    return () => {
      engine.removeEventListener('playNote', handlePlay)
    }
  }, [engine])

  // Create activeNotes for keyboard based on selected chord or scale
  const chordNotes = useMemo(() => {
    if (selectedChord) {
      const chordNotes = ChordLib.getNotes(selectedChord)
      const octaves = [3, 4, 5]
      return octaves.flatMap(octave => chordNotes.map(note => `${note}${octave}` as NoteOctave))
    } else if (scale) {
      const notes = ScaleLib.getNotes(scale)
      const octaves = [3, 4, 5]
      return octaves.flatMap(octave => notes.slice(0, 7).map(note => `${note}${octave}` as NoteOctave))
    }
    return []
  }, [scale, selectedChord])

  // Generate guitar fingerings for selected chord
  const chordFingerings = useMemo(
    () => (selectedChord ? GuitarLib.getFingeringWithMetadata(GuitarLib.getFingering(selectedChord) ?? [], scale) : []),
    [selectedChord, scale]
  )

  // Play a chord using the audio engine
  const playChord = useCallback(
    (chord: Chord) => {
      const notes: NoteEvent[] = ChordLib.getNotes(chord).map(n => ({
        type: 'note',
        note: `${n}4` as NoteName,
        duration: '4n',
      }))
      playNotes(notes)
    },
    [playNotes]
  )

  // Play a scale using the audio engine
  const playScale = async () => {
    if (!scale) {
      return
    }

    const notes = ScaleLib.getNotesWithOctave(scale, 4)
    const noteEvents: NoteEvent[] = notes.filter(Boolean).map(noteEvent => {
      const { note, octave } = getNoteAndOctave(noteEvent)
      return {
        type: 'note',
        note,
        octave,
        duration: '4n',
      } satisfies NoteEvent
    })

    playNoteSequence(noteEvents)
  }

  // Handle tab changes
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
  }

  // Handle chord selection
  const handleChordClick = (chord: ChordVariant) => {
    setSelectedChord(chord === selectedChord ? null : chord)
    playChord(chord)
  }

  // Clear selected chord and return to scale view
  const handleClearChord = () => {
    setSelectedChord(null)
  }

  const showNotes = activeNotes.length > 0 ? activeNotes : chordNotes.length > 0 ? chordNotes : scaleNotes

  return (
    <Box>
      {scale && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body1">
                {ScaleLib.getName(scale)}
                <IconButton color="primary" onClick={playScale} size="small" sx={{ ml: 1 }} title="Play scale">
                  <PlayArrow />
                </IconButton>
              </Typography>

              <Typography variant="body2" gutterBottom>
                <strong>Notes:</strong> {scaleNotes.map(note => NoteLib.prettifyNote(note)).join(' - ')}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Scale Intervals & notes
              </Typography>
              {/* Scale intervals */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {scaleNotes.slice(0, 7).map((note, index) => (
                  <Grid key={index}>
                    <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
                      <Note note={note} scale={scale} />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="subtitle1" gutterBottom>
                Chords in {scale.root} {scale.type} (click to view fingerings)
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {scaleChords.map((chord, index) => (
                  <Grid key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        variant={chord === selectedChord ? 'contained' : 'outlined'}
                        onClick={() => handleChordClick(chord)}
                        sx={{ minWidth: '60px', m: 0.5, textTransform: 'none' }}
                      >
                        <VBox>
                          <Typography variant="caption">
                            {ScaleLib.prettyScaleChordDegree(scale, ScaleLib.SCALE_DEGREE_NAMES[index])}
                          </Typography>
                          <Typography>
                            {NoteLib.prettifyNote(chord.root)}
                            {symbols.prettyPrint(chord.variant)}
                          </Typography>
                        </VBox>
                      </Button>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ justifyContent: 'center', display: 'flex' }}>
              <Box
                sx={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: '40vh',
                  maxHeight: '40vh',
                  display: 'flex',
                  aspectRatio: '1 / 1',
                }}
              >
                <CircleOfFifths scale={scale} />
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {selectedChord && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6">
                  Selected Chord: {NoteLib.prettifyNote(selectedChord.root)}
                  {selectedChord.variant}
                </Typography>
                <IconButton
                  color="primary"
                  onClick={() => playChord(selectedChord)}
                  size="small"
                  sx={{ ml: 1 }}
                  title="Play chord"
                >
                  <PlayArrow />
                </IconButton>
                <Button variant="text" color="inherit" onClick={handleClearChord} size="small" sx={{ ml: 2 }}>
                  (Back to Scale View)
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Notes:{' '}
                {ChordLib.getNotes(selectedChord)
                  .map(note => NoteLib.prettifyNote(note))
                  .join(', ')}
              </Typography>
              {chordFingerings.length > 0 && <GuitarChord chord={selectedChord} scale={scale} />}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Tabs value={selectedTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Guitar" />
            <Tab label="Keyboard" />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {selectedTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {selectedChord
                      ? `${NoteLib.prettifyNote(selectedChord.root)}${selectedChord.variant} Chord`
                      : `${scale.root} ${scale.type}`}{' '}
                  </Typography>
                </Box>

                <Fretboard
                  scale={scale}
                  fingerings={selectedChord ? chordFingerings : fingeringsFromScale}
                  labels="note"
                />
              </Box>
            )}

            {selectedTab === 1 && (
              <VBox>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h6">
                    {selectedChord
                      ? `${NoteLib.prettifyNote(selectedChord.root)}${selectedChord.variant} Chord`
                      : `${scale.root} ${scale.type} Scale`}{' '}
                    on Keyboard
                  </Typography>
                </Box>
                <Keyboard
                  scale={scale}
                  label="note-scale"
                  showLabelOnActiveOnly={showNotes.length > 0}
                  activeNotes={showNotes}
                  noteRange={{ firstNote: isSmallOrSmaller ? 'c4' : 'c3', lastNote: isSmallOrSmaller ? 'c5' : 'c6' }}
                  onPlayNote={info => {
                    const { note, octave } = getNoteAndOctave(info.note)
                    const noteEvent: NoteEvent = {
                      type: 'note',
                      octave,
                      note,
                      duration: '4n',
                    }
                    playNotes([noteEvent])
                  }}
                />
              </VBox>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default MusicTheory
